import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@gelirgider/ui';

export interface ConfirmOptions {
  title?: ReactNode;
  description?: ReactNode;
  confirmLabel?: ReactNode;
  cancelLabel?: ReactNode;
  danger?: boolean;
}

type ConfirmFn = (opts?: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

interface PendingState {
  options: ConfirmOptions;
  resolve: (ok: boolean) => void;
}

export function ConfirmProvider({ children }: { children: ReactNode }): React.ReactElement {
  const { t } = useTranslation('admin');
  const [pending, setPending] = useState<PendingState | null>(null);
  const pendingRef = useRef<PendingState | null>(null);

  const confirm = useCallback<ConfirmFn>((opts) => {
    return new Promise<boolean>((resolve) => {
      const next: PendingState = { options: opts ?? {}, resolve };
      pendingRef.current = next;
      setPending(next);
    });
  }, []);

  const close = useCallback((ok: boolean): void => {
    const current = pendingRef.current;
    if (current) current.resolve(ok);
    pendingRef.current = null;
    setPending(null);
  }, []);

  const value = useMemo(() => confirm, [confirm]);

  const danger = pending?.options.danger ?? false;
  const title = pending?.options.title ?? (danger ? t('confirm.dangerTitle') : t('confirm.title'));
  const description =
    pending?.options.description ??
    (danger ? t('confirm.dangerDescription') : t('confirm.description'));
  const confirmLabel = pending?.options.confirmLabel ?? t('confirm.yes');
  const cancelLabel = pending?.options.cancelLabel ?? t('confirm.cancel');

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <Dialog open={pending !== null} onOpenChange={(open) => !open && close(false)}>
        {pending !== null ? (
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{title}</DialogTitle>
              <DialogDescription>{description}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => close(false)} autoFocus>
                {cancelLabel}
              </Button>
              <Button variant={danger ? 'destructive' : 'default'} onClick={() => close(true)}>
                {confirmLabel}
              </Button>
            </DialogFooter>
          </DialogContent>
        ) : null}
      </Dialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm() must be used within <ConfirmProvider>');
  return ctx;
}
